<script lang="ts">
import type { ICellRendererParams } from 'ag-grid-community';
import { defineComponent, ref, type PropType } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import type { DisplayValueType } from '@/models/common/ValueTypes';
import { useFuzzySearch } from '@/composables/useFuzzySearch';

type ExtendedParams = ICellRendererParams<any, void> & {
    selectedValue: DisplayValueType<string | null>;
    options: DisplayValueType<string | null>[],
    showSearch?: boolean;
};

export default defineComponent({
    directives: {
        onClickOutside: vOnClickOutside
    },
    props: {
        params: {
            type: Object as PropType<ExtendedParams>,
            required: true
        }
    },
    setup(props) {
        const { searchString, filteredItems: filteredOptions } = useFuzzySearch(() => props.params.options);

        const internalValue = ref<DisplayValueType<string | null> | null>(props.params.selectedValue);
        const selectedValue = ref<string | null>(props.params.selectedValue.value);

        function select(option: DisplayValueType<string | null>): void {
            selectedValue.value = option.value;
            internalValue.value = option;
            props.params.api.stopEditing();
        }

        function closePopup(): void {
            props.params.api.stopEditing();
        }

        function getValue(): string | null {
            return selectedValue.value;
        }

        return {
            searchString,
            filteredOptions,
            internalValue,
            select,
            closePopup,
            getValue
        };
    }
});
</script>

<template>
    <div v-on-click-outside="closePopup" class="dropdown-menu">
        <template v-if="params.showSearch !== false">
            <SDropdownItemSearch v-model="searchString" />
            <SDropdownDivider />
        </template>
        <SDropdownItem
            v-for="option of filteredOptions"
            :key="option.value"
            :is-active="internalValue?.value === option.value"
            @click="select(option)"
        >
            {{ option.display }}
        </SDropdownItem>
    </div>
</template>
